import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faUpload, faSearch, faLink,
  faTimes, faEdit, faTrash, faFileExcel, faFileCsv,
  faUsers, faTimesCircle, faCheck, faSort, faSortUp, faSortDown,
  faUndo, faTrashRestore, faKey, faUserPlus, faChevronLeft, faChevronRight,
  faGavel, faLock, faEye, faEyeSlash, faLinkSlash
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../services/api';
import { sendPasswordReset, createPortalForSupplier, setSupplierPassword } from '../services/supplierApi';
import { unlinkSupplier } from '../services/glnLinkingApi';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import SupplierFormModal from '../components/suppliers/SupplierFormModal';
import SupplierImportModal from '../components/suppliers/SupplierImportModal';
import DeleteConfirmModal from '../components/suppliers/DeleteConfirmModal';
import { GLNLinkingModal } from '../components/suppliers/GLNLinkingModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Supplier, SupplierFormData } from '../types/supplier';

interface DeleteModalData {
  type: 'single' | 'bulk';
  supplierId?: string;
  supplierName?: string;
  count?: number;
  isPermanent?: boolean;
}

const Suppliers: React.FC = () => {
  // State declarations
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter & Search States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<string>('true');
  const [filterSource, setFilterSource] = useState<string>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [wasSearchFocused, setWasSearchFocused] = useState(false);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Pagination States
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGLNLinkingModal, setShowGLNLinkingModal] = useState(false);
  const [glnLinkingSearch, setGlnLinkingSearch] = useState('');
  const [unlinkTarget, setUnlinkTarget] = useState<{ id: string; name: string; masterName: string } | null>(null);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // Selection for bulk actions
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);

  // Delete Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<DeleteModalData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Password Reset States
  const [passwordResetLoading, setPasswordResetLoading] = useState<string | null>(null);

  // Manual Password States
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [setPasswordSupplier, setSetPasswordSupplier] = useState<Supplier | null>(null);
  const [manualPassword, setManualPassword] = useState('');
  const [showManualPassword, setShowManualPassword] = useState(false);
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordError, setSetPasswordError] = useState<string | null>(null);
  
  // Portal Creation States
  const [portalCreationLoading, setPortalCreationLoading] = useState<string | null>(null);

  // Debounced search state
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Data Loading
  const loadSuppliers = useCallback(async (customSearchTerm?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSuppliers({
        page: currentPage,
        limit: itemsPerPage,
        search: customSearchTerm !== undefined ? customSearchTerm : debouncedSearchTerm,
        isActive: filterActive,
        importSource: filterSource,
        sortBy,
        sortOrder
      });
      
      console.log('📊 Suppliers API Response:', response.data);
      
      // Handle optimized API response structure
      if (response.data) {
        const { suppliers = [], pagination } = response.data;
        
        console.log('📊 Pagination info:', pagination);
        
        setSuppliers(suppliers);
        setTotalSuppliers(pagination?.total || 0);
        setTotalPages(pagination?.pages || 0);
        
        // Debug: Check first supplier in detail
        if (suppliers.length > 0) {
          console.log('🔍 First supplier details:', suppliers[0]);
          console.log('🔍 hasPortal field:', suppliers[0].hasPortal);
          console.log('🔍 masterSupplierId field:', suppliers[0].masterSupplierId);
        }
      } else {
        setSuppliers([]);
        setTotalSuppliers(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError('Fehler beim Laden der Lieferanten');
      setSuppliers([]);
      setTotalSuppliers(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filterActive, filterSource, sortBy, sortOrder, debouncedSearchTerm]);
  
  // Debounce searchTerm
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Load suppliers when dependencies change (including debounced search)
  useEffect(() => {
    loadSuppliers();
  }, [currentPage, itemsPerPage, filterActive, filterSource, sortBy, sortOrder, debouncedSearchTerm]);

  // Restore focus after loading if search was focused
  useEffect(() => {
    if (!loading && wasSearchFocused && searchInputRef.current) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [loading, wasSearchFocused]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterActive, filterSource]);

  // Stabilized search handler
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Focus tracking handlers
  const handleSearchFocus = useCallback(() => {
    setWasSearchFocused(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setWasSearchFocused(false);
  }, []);

  // Filter Handlers
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterActive('true');
    setFilterSource('all');
    setSortBy('name');
    setSortOrder('asc');
    setCurrentPage(1);
    setSelectedSuppliers([]);
  };

  // Selection Handlers
  const handleSelectSupplier = (supplierId: string) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId)
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSelectAll = () => {
    const allIds = suppliers.map(sup => sup._id!);
    setSelectedSuppliers(prev => 
      prev.length === allIds.length ? [] : allIds
    );
  };

  // Event Handlers
  const handleCreate = () => {
    setSelectedSupplier(null);
    setShowCreateModal(true);
  };

  const handleEdit = (supplier: Supplier, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedSupplier(supplier);
    setShowEditModal(true);
  };

  const handleCreateSave = useCallback(async (supplierData: SupplierFormData) => {
    try {
      await createSupplier(supplierData);
      await loadSuppliers();
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  }, [loadSuppliers]);

  const handleEditSave = useCallback(async (supplierData: SupplierFormData) => {
    if (!selectedSupplier?._id) return;
    
    try {
      await updateSupplier(selectedSupplier._id, supplierData);
      await loadSuppliers();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  }, [selectedSupplier, loadSuppliers]);

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleImportComplete = () => {
    loadSuppliers();
  };

  // Reactivation Functions
  const handleReactivate = async (supplier: Supplier, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await updateSupplier(supplier._id!, { ...supplier, isActive: true });
      await loadSuppliers();
    } catch (error) {
      console.error('Error reactivating supplier:', error);
      setError('Fehler beim Reaktivieren des Lieferanten');
    }
  };

  const handleBulkReactivate = async () => {
    if (selectedSuppliers.length === 0) return;
    
    try {
      await Promise.all(
        selectedSuppliers.map(id => {
          const supplier = suppliers.find(s => s._id === id);
          if (supplier) {
            return updateSupplier(id, { ...supplier, isActive: true });
          }
          return Promise.resolve();
        })
      );
      setSelectedSuppliers([]);
      await loadSuppliers();
    } catch (error) {
      console.error('Error bulk reactivating suppliers:', error);
      setError('Fehler beim Reaktivieren der Lieferanten');
    }
  };

  // Password Reset Handler
  const handlePasswordReset = async (supplier: Supplier, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!supplier._id) return;
    
    // Check if supplier has portal
    if (!supplier.hasPortal) {
      setError('Dieser Lieferant hat kein Portal und kann daher kein Passwort-Reset erhalten.');
      return;
    }
    
    try {
      setPasswordResetLoading(supplier._id);
      
      const result = await sendPasswordReset(supplier._id);
      
      if (result.success) {
        setSuccess(`Passwort-Reset Email wurde erfolgreich an ${supplier.email} gesendet.`);
        setError(null);
        setTimeout(() => setSuccess(null), 5000);
      }
      
    } catch (error) {
      console.error('Error sending password reset:', error);
      setError('Fehler beim Senden des Passwort-Resets');
    } finally {
      setPasswordResetLoading(null);
    }
  };

  // Open Manual Password Modal
  const handleOpenSetPassword = (supplier: Supplier, event: React.MouseEvent) => {
    event.stopPropagation();
    setSetPasswordSupplier(supplier);
    setManualPassword('');
    setShowManualPassword(false);
    setSetPasswordError(null);
    setShowSetPasswordModal(true);
  };

  // Generate a secure random password (min 12 chars, 1 uppercase, 3 numbers, 1 special)
  const generateRandomPassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*(),.?";:{}|<>';
    const all = uppercase + lowercase + numbers + special;
    let pwd = '';
    pwd += uppercase[Math.floor(Math.random() * uppercase.length)];
    pwd += numbers[Math.floor(Math.random() * numbers.length)];
    pwd += numbers[Math.floor(Math.random() * numbers.length)];
    pwd += numbers[Math.floor(Math.random() * numbers.length)];
    pwd += special[Math.floor(Math.random() * special.length)];
    for (let i = pwd.length; i < 14; i++) {
      pwd += all[Math.floor(Math.random() * all.length)];
    }
    pwd = pwd.split('').sort(() => Math.random() - 0.5).join('');
    setManualPassword(pwd);
    setShowManualPassword(true);
    setSetPasswordError(null);
  };

  // Save the manually entered password
  const handleSaveManualPassword = async () => {
    if (!setPasswordSupplier?._id) return;

    // Client-side validation matching backend requirements
    const errors: string[] = [];
    if (manualPassword.length < 12) {
      errors.push('mindestens 12 Zeichen');
    }
    if (!/[A-Z]/.test(manualPassword)) {
      errors.push('einen Großbuchstaben');
    }
    if ((manualPassword.match(/\d/g) || []).length < 3) {
      errors.push('mindestens 3 Zahlen');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(manualPassword)) {
      errors.push('ein Sonderzeichen');
    }
    if (errors.length > 0) {
      setSetPasswordError(`Das Passwort muss enthalten: ${errors.join(', ')}.`);
      return;
    }

    try {
      setSetPasswordLoading(true);
      setSetPasswordError(null);

      const result = await setSupplierPassword(setPasswordSupplier._id, manualPassword, false);

      if (result.success) {
        setSuccess(`Passwort für ${setPasswordSupplier.name} wurde erfolgreich gesetzt.`);
        setError(null);
        setShowSetPasswordModal(false);
        setSetPasswordSupplier(null);
        setManualPassword('');
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (error: any) {
      console.error('Error setting manual password:', error);
      setSetPasswordError(error.message || 'Fehler beim Setzen des Passworts');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  // Verknüpfung entfernen
  const handleUnlinkConfirm = async () => {
    if (!unlinkTarget) return;
    try {
      setUnlinkLoading(true);
      await unlinkSupplier(unlinkTarget.id);
      setSuccess(`"${unlinkTarget.name}" wurde von "${unlinkTarget.masterName}" entverknüpft.`);
      setError(null);
      setUnlinkTarget(null);
      await loadSuppliers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Fehler beim Entfernen der Verknüpfung');
    } finally {
      setUnlinkLoading(false);
    }
  };

  const openLinkingOverview = (search: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setGlnLinkingSearch(search);
    setShowGLNLinkingModal(true);
  };

  // Portal Creation Function
  const handleCreatePortal = async (supplier: Supplier, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!supplier._id) return;
    
    try {
      setPortalCreationLoading(supplier._id);
      
      const result = await createPortalForSupplier(supplier._id);
      
      if (result.message) {
        setSuccess(`Portal wurde erfolgreich für ${supplier.name} erstellt.`);
        setError(null);
        
        // Reload suppliers to update portal status
        await loadSuppliers();
        
        setTimeout(() => setSuccess(null), 5000);
      }
      
    } catch (error: any) {
      console.error('Error creating portal:', error);
      setError(error.message || 'Fehler beim Erstellen des Portals');
    } finally {
      setPortalCreationLoading(null);
    }
  };

  // Delete Functions
  const handleBulkDelete = () => {
    if (selectedSuppliers.length === 0) return;
    
    const selectedSupplierObjects = suppliers.filter(s => selectedSuppliers.includes(s._id!));
    const allInactive = selectedSupplierObjects.every(s => !s.isActive);
    
    setDeleteModalData({
      type: 'bulk',
      count: selectedSuppliers.length,
      isPermanent: allInactive
    });
    setShowDeleteModal(true);
  };

  const handleDelete = (supplier: Supplier, event: React.MouseEvent) => {
    event.stopPropagation();
    
    setDeleteModalData({
      type: 'single',
      supplierId: supplier._id!,
      supplierName: supplier.name,
      isPermanent: !supplier.isActive
    });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalData) return;

    try {
      setDeleteLoading(true);
      
      if (deleteModalData.type === 'bulk') {
        if (deleteModalData.isPermanent) {
          await Promise.all(
            selectedSuppliers.map(id => deleteSupplier(id))
          );
        } else {
          await Promise.all(
            selectedSuppliers.map(id => {
              const supplier = suppliers.find(s => s._id === id);
              if (supplier) {
                return updateSupplier(id, { ...supplier, isActive: false });
              }
              return Promise.resolve();
            })
          );
        }
        setSelectedSuppliers([]);
      } else if (deleteModalData.type === 'single' && deleteModalData.supplierId) {
        if (deleteModalData.isPermanent) {
          await deleteSupplier(deleteModalData.supplierId);
        } else {
          const supplier = suppliers.find(s => s._id === deleteModalData.supplierId);
          if (supplier) {
            await updateSupplier(deleteModalData.supplierId, { ...supplier, isActive: false });
          }
        }
      }
      
      await loadSuppliers();
      setShowDeleteModal(false);
      setDeleteModalData(null);
      
    } catch (error) {
      console.error('Error deleting supplier(s):', error);
      setError('Fehler beim Löschen der Lieferanten');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setDeleteModalData(null);
  };

  const getDeleteModalContent = () => {
    if (!deleteModalData) return { title: '', message: '' };

    if (deleteModalData.type === 'bulk') {
      const action = deleteModalData.isPermanent ? 'endgültig löschen' : 'deaktivieren';
      return {
        title: deleteModalData.isPermanent ? 'Lieferanten endgültig löschen' : 'Lieferanten deaktivieren',
        message: `Möchten Sie ${deleteModalData.count} Lieferant${deleteModalData.count === 1 ? '' : 'en'} wirklich ${action}?`
      };
    } else {
      const action = deleteModalData.isPermanent ? 'endgültig löschen' : 'deaktivieren';
      return {
        title: deleteModalData.isPermanent ? 'Lieferant endgültig löschen' : 'Lieferant deaktivieren',
        message: `Möchten Sie "${deleteModalData.supplierName}" wirklich ${action}?`
      };
    }
  };

  // Helper Functions
  const getSourceLabel = (source: string): string => {
    const sources: Record<string, string> = {
      manual: 'Manuell',
      excel: 'Excel-Import',
      csv: 'CSV-Import',
      bc_interface: 'BC-Schnittstelle'
    };
    return sources[source] || source;
  };

  const getSourceIcon = (source: string): IconDefinition => {
    const icons: Record<string, IconDefinition> = {
      manual: faEdit,
      excel: faFileExcel,
      csv: faFileCsv,
      bc_interface: faUsers
    };
    return icons[source] || faUsers;
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <FontAwesomeIcon icon={faSort} className="text-gray-400" />;
    return <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="text-gray-600" />;
  };

  const getBulkActions = () => {
    const selectedSupplierObjects = suppliers.filter(s => selectedSuppliers.includes(s._id!));
    const hasInactive = selectedSupplierObjects.some(s => !s.isActive);
    const hasActive = selectedSupplierObjects.some(s => s.isActive);
    
    return (
      <div className="flex items-center gap-1">
        {hasInactive && (
          <button
            onClick={handleBulkReactivate}
            className="inline-flex items-center px-2 py-1 bg-green-500 text-white rounded text-xs font-medium hover:bg-green-600 transition-all duration-150 shadow-sm"
          >
            <FontAwesomeIcon icon={faTrashRestore} className="mr-1" />
            Reaktivieren
          </button>
        )}
        
        {hasActive && (
          <button
            onClick={handleBulkDelete}
            className="inline-flex items-center px-2 py-1 bg-yellow-500 text-white rounded text-xs font-medium hover:bg-yellow-600 transition-all duration-150 shadow-sm"
          >
            <FontAwesomeIcon icon={faUndo} className="mr-1" />
            Deaktivieren
          </button>
        )}
        
        <button
          onClick={handleBulkDelete}
          className="inline-flex items-center px-2 py-1 bg-red-500 text-white rounded text-xs font-medium hover:bg-red-600 transition-all duration-150 shadow-sm"
        >
          <FontAwesomeIcon icon={faTrash} className="mr-1" />
          {selectedSupplierObjects.every(s => !s.isActive) ? 'Endgültig löschen' : 'Löschen'}
        </button>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Lieferanten werden geladen..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-2.5 text-gray-400 text-sm" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Lieferanten durchsuchen..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent bg-white text-sm"
            />
          </div>
          
          {/* Filter and Actions */}
          <div className="flex gap-2">
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="all">Alle Status</option>
              <option value="true">Aktiv</option>
              <option value="false">Inaktiv</option>
            </select>
            
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent bg-white text-sm min-w-[150px]"
            >
              <option value="all">Alle Quellen</option>
              <option value="manual">Manuell</option>
              <option value="excel">Excel-Import</option>
              <option value="csv">CSV-Import</option>
              <option value="bc_interface">BC-Schnittstelle</option>
            </select>
            
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors text-sm"
              title="Filter zurücksetzen"
            >
              <FontAwesomeIcon icon={faTimesCircle} />
            </button>
            
            <button
              onClick={handleImport}
              className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <FontAwesomeIcon icon={faUpload} className="mr-1.5 text-xs" />
              Import
            </button>
            
            <button
              onClick={() => {
                setGlnLinkingSearch('');
                setShowGLNLinkingModal(true);
              }}
              className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
            >
              <FontAwesomeIcon icon={faLink} className="mr-1.5 text-xs" />
              GLN Verknüpfung
            </button>
            
            <button
              onClick={handleCreate}
              className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1.5 text-xs" />
              Neuer Lieferant
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mx-4 mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">✅ {success}</p>
        </div>
      )}
      
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">❌ {error}</p>
        </div>
      )}

      {/* Bulk Actions */}
      <AnimatePresence>
        {selectedSuppliers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mx-4 mb-4 mt-4"
          >
            <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg p-2 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {selectedSuppliers.length}
                    </div>
                    <span className="text-sm font-semibold text-red-900">
                      {selectedSuppliers.length === 1 ? 'Lieferant ausgewählt' : 'Lieferanten ausgewählt'}
                    </span>
                  </div>
                  
                  <div className="h-4 w-px bg-red-300"></div>
                  
                  {getBulkActions()}
                </div>
                
                <button
                  onClick={() => setSelectedSuppliers([])}
                  className="text-red-600 hover:text-red-800 hover:bg-red-200 rounded-full p-1 transition-all duration-150"
                  title="Auswahl aufheben"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Area */}
      <div className="p-4">
        {suppliers.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="text-gray-400 mb-4">
              <FontAwesomeIcon icon={faUsers} className="text-4xl mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filterActive !== 'all' || filterSource !== 'all' 
                  ? 'Keine Lieferanten gefunden' 
                  : 'Noch keine Lieferanten vorhanden'}
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {searchTerm || filterActive !== 'all' || filterSource !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien oder Filter'
                  : 'Erstellen Sie Ihren ersten Lieferanten oder importieren Sie eine Liste'}
              </p>
            </div>
            {!searchTerm && filterActive === 'true' && filterSource === 'all' && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1.5 text-xs" />
                  Lieferant erstellen
                </button>
                <button
                  onClick={handleImport}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <FontAwesomeIcon icon={faUpload} className="mr-1.5 text-xs" />
                  Liste importieren
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-2 py-2">
              <div className="grid grid-cols-12 gap-2 items-center text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <div className="col-span-1 flex justify-start pl-2">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.length === suppliers.length && suppliers.length > 0}
                      onChange={handleSelectAll}
                      className="sr-only"
                      id="select-all-suppliers"
                    />
                    <label
                      htmlFor="select-all-suppliers"
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        selectedSuppliers.length === suppliers.length && suppliers.length > 0
                          ? 'bg-red-500 border-red-500'
                          : 'border-gray-300 hover:border-red-400'
                      }`}
                    >
                      {selectedSuppliers.length === suppliers.length && suppliers.length > 0 && (
                        <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                      )}
                    </label>
                  </div>
                </div>
                <div className="col-span-2 text-left pl-2">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Name {getSortIcon('name')}
                  </button>
                </div>
                <div className="col-span-2 text-left pl-2">
                  <button
                    onClick={() => handleSort('email')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    E-Mail {getSortIcon('email')}
                  </button>
                </div>
                <div className="col-span-1 text-left pl-2">
                  EK
                </div>
                <div className="col-span-1 text-left pl-2">
                  <button
                    onClick={() => handleSort('isActive')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Status {getSortIcon('isActive')}
                  </button>
                </div>
                <div className="col-span-1 text-left pl-2">
                  Typ
                </div>
                <div className="col-span-1 text-left pl-2">
                  <button
                    onClick={() => handleSort('importSource')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Quelle {getSortIcon('importSource')}
                  </button>
                </div>
                <div className="col-span-1 text-left pl-2">
                  <button
                    onClick={() => handleSort('createdAt')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Erstellt {getSortIcon('createdAt')}
                  </button>
                </div>
                <div className="col-span-2 text-right pr-4">
                  Aktionen
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-100">
              {suppliers.map((supplier, index) => {
                const isSelected = selectedSuppliers.includes(supplier._id!);
                
                return (
                  <motion.div
                    key={supplier._id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.01 }}
                    className={`grid grid-cols-12 gap-2 items-center px-2 py-2 hover:bg-gray-50 transition-all duration-150 text-sm ${
                      isSelected ? 'bg-red-50 border-l-4 border-red-400' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="col-span-1 flex justify-start pl-2">
                      <div 
                        className="relative cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectSupplier(supplier._id!);
                        }}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          isSelected
                            ? 'bg-red-500 border-red-500'
                            : 'border-gray-300 hover:border-red-400'
                        }`}>
                          {isSelected && (
                            <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Clickable Content */}
                    <div 
                      className="col-span-9 grid grid-cols-9 gap-2 items-center cursor-pointer"
                      onClick={() => handleEdit(supplier, { stopPropagation: () => {} } as React.MouseEvent)}
                    >
                      {/* Name */}
                      <div className="col-span-2 text-left pl-2">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon 
                            icon={getSourceIcon(supplier.importSource || 'manual')} 
                            className="text-base text-red-500" 
                          />
                          <div className="min-w-0 flex-1">
                            <div className={`font-medium truncate ${supplier.isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                              {supplier.name}
                            </div>
                            {supplier.gln && (
                              <div className="text-xs text-gray-500">
                                GLN: {supplier.gln}
                              </div>
                            )}
                            {supplier.kreditorNumber && (
                              <div className="text-xs text-gray-500">
                                Kreditor: {supplier.kreditorNumber}
                              </div>
                            )}
                            {supplier.masterSupplierId && (
                              <button
                                type="button"
                                onClick={(e) => openLinkingOverview(supplier.masterSupplierName || supplier.name, e)}
                                className={`text-xs truncate max-w-full text-left hover:underline ${supplier.masterSupplierMissing ? 'text-red-600' : 'text-blue-700'}`}
                                title="In der Verknüpfungsübersicht anzeigen"
                              >
                                <FontAwesomeIcon icon={faLink} className="mr-1" />
                                {supplier.masterSupplierMissing
                                  ? `Hauptkonto fehlt/inaktiv${supplier.masterSupplierName ? `: ${supplier.masterSupplierName}` : ''}`
                                  : supplier.masterSupplierName
                                    ? `Unterkonto von ${supplier.masterSupplierName}`
                                    : 'Unterkonto (Hauptkonto anzeigen)'}
                              </button>
                            )}
                            {!supplier.masterSupplierId && supplier.linkedSubAccounts && supplier.linkedSubAccounts.length > 0 && (
                              <button
                                type="button"
                                onClick={(e) => openLinkingOverview(supplier.name, e)}
                                className="text-xs text-blue-700 truncate max-w-full text-left hover:underline"
                                title={supplier.linkedSubAccounts.map(s => s.name).join('\n')}
                              >
                                <FontAwesomeIcon icon={faLink} className="mr-1" />
                                Verknüpft: {supplier.linkedSubAccounts.map(s => s.name).join(', ')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="col-span-2 text-left pl-2">
                        <div className={`truncate text-xs ${supplier.isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                          {supplier.email && !supplier.email.endsWith('@supplier-import.local') && !supplier.email.endsWith('@perfion-import.local') ? (
                            <a
                              href={`mailto:${supplier.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:underline hover:text-blue-800"
                              title={supplier.email}
                            >
                              {supplier.email}
                            </a>
                          ) : (
                            <span className={supplier.email?.includes('@supplier-import.local') || supplier.email?.includes('@perfion-import.local') ? 'text-gray-400 italic' : ''}>
                              {supplier.email?.includes('@supplier-import.local') || supplier.email?.includes('@perfion-import.local') ? '–' : supplier.email}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* EK Areas */}
                      <div className="col-span-1 text-left pl-2">
                        <div className="flex flex-wrap gap-1">
                          {supplier.assignedEK && supplier.assignedEK.length > 0 ? (
                            supplier.assignedEK.slice(0, 2).map((ek, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {ek}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                          {supplier.assignedEK && supplier.assignedEK.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{supplier.assignedEK.length - 2}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-span-1 text-left pl-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          supplier.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {supplier.isActive ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>

                      {/* Typ */}
                      <div className="col-span-1 text-left pl-2">
                        <div className="flex items-center gap-1">
                          {supplier.masterSupplierId ? (
                            <span
                              className="px-2 py-0.5 text-xs rounded-full font-medium bg-blue-100 text-blue-800"
                              title={supplier.masterSupplierName ? `Unterkonto von ${supplier.masterSupplierName}` : undefined}
                            >
                              Verknüpft
                            </span>
                          ) : (
                            <span
                              className="px-2 py-0.5 text-xs rounded-full font-medium bg-yellow-100 text-yellow-800"
                              title={supplier.linkedSubAccounts?.length ? `${supplier.linkedSubAccounts.length} Unterkonto(en)` : undefined}
                            >
                              Hauptkonto{supplier.linkedSubAccounts?.length ? ` · ${supplier.linkedSubAccounts.length}` : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Source */}
                      <div className="col-span-1 text-left pl-2">
                        <div className={`text-xs ${supplier.isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                          {getSourceLabel(supplier.importSource || 'manual')}
                        </div>
                      </div>

                      {/* Created */}
                      <div className="col-span-1 text-left pl-2">
                        <div className={`text-xs ${supplier.isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                          {supplier.createdAt 
                            ? new Date(supplier.createdAt).toLocaleDateString('de-DE')
                            : 'N/A'
                          }
                        </div>
                      </div>
                    </div>

                      {/* Actions - Outside clickable area */}
                      <div className="col-span-2 text-right pr-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={(e) => handleEdit(supplier, e)}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium transition-colors"
                            title="Bearbeiten"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      
                          </button>
                          
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await updateSupplier(supplier._id!, {
                                  canAuction: !supplier.canAuction,
                                  auctionEnabledAt: !supplier.canAuction ? new Date().toISOString() : undefined
                                });
                                setSuccess(`Auktionsberechtigung ${!supplier.canAuction ? 'aktiviert' : 'deaktiviert'}`);
                                loadSuppliers();
                              } catch (error: any) {
                                setError('Fehler beim Aktualisieren');
                              }
                            }}
                            className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                              supplier.canAuction
                                ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                            }`}
                            title={supplier.canAuction ? 'Auktionen deaktivieren' : 'Auktionen aktivieren'}
                          >
                            <FontAwesomeIcon icon={faGavel} />
                          </button>
                          
                          <button
                            onClick={(e) => handlePasswordReset(supplier, e)}
                            disabled={passwordResetLoading === supplier._id || !supplier.hasPortal}
                            className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                              supplier.hasPortal 
                                ? 'bg-orange-50 text-orange-700 hover:bg-orange-100' 
                                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                            } ${passwordResetLoading === supplier._id ? 'opacity-50' : ''}`}
                            title={supplier.hasPortal ? "Passwort per Email zusenden" : "Kein Portal verfügbar"}
                          >
                            <FontAwesomeIcon 
                              icon={faKey} 
                              className={`mr-1 ${passwordResetLoading === supplier._id ? 'animate-spin' : ''}`} 
                            />
                          </button>

                          <button
                            onClick={(e) => handleOpenSetPassword(supplier, e)}
                            disabled={!supplier.hasPortal}
                            className={`px-2 py-1 text-xs rounded font-medium transition-colors ${
                              supplier.hasPortal
                                ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                            }`}
                            title={supplier.hasPortal ? "Passwort manuell vergeben" : "Kein Portal verfügbar"}
                          >
                            <FontAwesomeIcon icon={faLock} />
                          </button>
                          
                          {supplier.masterSupplierId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setUnlinkTarget({
                                  id: supplier._id!,
                                  name: supplier.name,
                                  masterName: supplier.masterSupplierName || 'Hauptkonto'
                                });
                              }}
                              className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 font-medium transition-colors"
                              title={`Verknüpfung zu ${supplier.masterSupplierName || 'Hauptkonto'} entfernen`}
                            >
                              <FontAwesomeIcon icon={faLinkSlash} />
                            </button>
                          )}

                          {!supplier.masterSupplierId && !supplier.hasPortal && (
                            <button
                              onClick={(e) => handleCreatePortal(supplier, e)}
                              disabled={portalCreationLoading === supplier._id}
                              className={`px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 font-medium transition-colors ${
                                portalCreationLoading === supplier._id ? 'opacity-50' : ''
                              }`}
                              title="Portal erstellen"
                            >
                              <FontAwesomeIcon 
                                icon={faUserPlus} 
                                className={`mr-1 ${portalCreationLoading === supplier._id ? 'animate-spin' : ''}`} 
                              />
                            </button>
                          )}
                          
                          {!supplier.isActive && (
                            <button
                              onClick={(e) => handleReactivate(supplier, e)}
                              className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 font-medium transition-colors"
                              title="Reaktivieren"
                            >
                              <FontAwesomeIcon icon={faTrashRestore} className="mr-1" />
                            </button>
                          )}
                          
                          <button
                            onClick={(e) => handleDelete(supplier, e)}
                            className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 font-medium transition-colors"
                            title={supplier.isActive ? 'Deaktivieren' : 'Endgültig löschen'}
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                          </button>
                        </div>
                      </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination */}
            {!loading && suppliers.length > 0 && totalPages > 1 && (
              <div className="bg-white px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-700">
                    <span>
                      Zeige {((currentPage - 1) * itemsPerPage) + 1} bis {Math.min(currentPage * itemsPerPage, totalSuppliers)} von {totalSuppliers} Lieferanten
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                      <span className="ml-1">Zurück</span>
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === currentPage
                              ? 'z-10 bg-red-50 border-red-500 text-red-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <span className="mr-1">Weiter</span>
                      <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <SupplierFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateSave}
        mode="create"
      />

      <SupplierFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditSave}
        supplier={selectedSupplier}
        mode="edit"
      />

      <SupplierImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={handleImportComplete}
      />

      <GLNLinkingModal
        isOpen={showGLNLinkingModal}
        onClose={() => setShowGLNLinkingModal(false)}
        onLinkingComplete={loadSuppliers}
        initialTab={glnLinkingSearch ? 'view' : 'organize'}
        initialViewSearch={glnLinkingSearch}
      />

      <DeleteConfirmModal
        isOpen={!!unlinkTarget}
        onClose={() => !unlinkLoading && setUnlinkTarget(null)}
        onConfirm={handleUnlinkConfirm}
        title="Verknüpfung entfernen"
        message={unlinkTarget
          ? `"${unlinkTarget.name}" von "${unlinkTarget.masterName}" entverknüpfen? Das Konto wird danach nicht mehr automatisch per E-Mail verknüpft.`
          : ''}
        confirmText="Entverknüpfen"
        loading={unlinkLoading}
      />

      {/* Manual Password Modal */}
      <AnimatePresence>
        {showSetPasswordModal && setPasswordSupplier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => !setPasswordLoading && setShowSetPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-lg bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <h3 className="flex items-center text-lg font-semibold text-gray-900">
                  <FontAwesomeIcon icon={faLock} className="mr-2 text-blue-600" />
                  Passwort manuell vergeben
                </h3>
                <button
                  onClick={() => setShowSetPasswordModal(false)}
                  disabled={setPasswordLoading}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4">
                <p className="text-sm text-gray-600">
                  Vergeben Sie ein Passwort für <span className="font-semibold">{setPasswordSupplier.name}</span>.
                  Der Lieferant kann sich anschließend mit der E-Mail{' '}
                  <span className="font-medium">{setPasswordSupplier.email}</span> und diesem Passwort anmelden.
                </p>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <input
                      type={showManualPassword ? 'text' : 'password'}
                      value={manualPassword}
                      onChange={(e) => {
                        setManualPassword(e.target.value);
                        setSetPasswordError(null);
                      }}
                      placeholder="Passwort eingeben"
                      autoComplete="new-password"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowManualPassword(!showManualPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      title={showManualPassword ? 'Verbergen' : 'Anzeigen'}
                    >
                      <FontAwesomeIcon icon={showManualPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generateRandomPassword}
                    className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800"
                  >
                    Sicheres Passwort generieren
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  Mindestens 12 Zeichen, 1 Großbuchstabe, 3 Zahlen und 1 Sonderzeichen.
                </p>

                {setPasswordError && (
                  <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                    {setPasswordError}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={() => setShowSetPasswordModal(false)}
                  disabled={setPasswordLoading}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveManualPassword}
                  disabled={setPasswordLoading || !manualPassword}
                  className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {setPasswordLoading && (
                    <FontAwesomeIcon icon={faKey} className="mr-2 animate-spin" />
                  )}
                  Passwort speichern
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {showDeleteModal && deleteModalData && (
          <DeleteConfirmModal
            isOpen={showDeleteModal}
            onClose={handleCloseDeleteModal}
            onConfirm={handleDeleteConfirm}
            title={getDeleteModalContent().title}
            message={getDeleteModalContent().message}
            loading={deleteLoading}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Suppliers;